import React, { useState } from 'react';
import { Button, message } from 'antd';
import RichTextEditor from '../../../components/RichTextEditor';
import {
	useCompleteProgramReviewMutation,
	useCreateProgramIssueMutation,
	useGetProgramIssuesQuery,
	useGetProgramQuery,
	useStartProgramReviewMutation,
} from '../../../redux/api/manufacturing';
import { useGetLoggedInUserQuery } from '../../../redux/api/users';
import {
	DEFAULT_FAULT,
	DEFAULT_SEVERITY,
	hasIssueDescription,
} from '../issueFields';
import { useProgramImages } from '../useProgramImages';
import EmbedHeader from './EmbedHeader';
import EmbedIssueList from './EmbedIssueList';
import EmbedReviewFeedback from './EmbedReviewFeedback';
import ReviewClock from './ReviewClock';
import { REVIEW_PHASE_LABEL, reviewPhaseFor } from './reviewPhase';

import './embed.css';

interface EmbedProgramIssueLogPageProps {
	programId: string;
}

/**
 * Minimal issue-logging surface for embedding in Fusion 360 or other narrow
 * hosts. The reviewer starts the clock, logs what they find, and ends the
 * review; nothing else is on screen.
 */
const EmbedProgramIssueLogPage: React.FC<EmbedProgramIssueLogPageProps> = ({
	programId,
}) => {
	const [description, setDescription] = useState('');
	const [editorKey, setEditorKey] = useState(0);

	const {
		data: program,
		isLoading: isLoadingProgram,
		isError: isProgramError,
	} = useGetProgramQuery(programId, { skip: !programId });

	// The phase hangs on who is signed in, and this page remounts after every
	// sign-in, so refetching on mount is what keeps the two in step when one
	// reviewer hands the palette to another.
	const { data: currentUser } = useGetLoggedInUserQuery(undefined, {
		refetchOnMountOrArgChange: true,
	});

	const phase =
		program && currentUser ? reviewPhaseFor(program, currentUser.id) : null;

	const reviewId =
		phase === 'review'
			? program?.first_review_id ?? null
			: phase === 'secondary_review'
			? program?.secondary_review_id ?? null
			: null;

	// Found by id rather than by position, so this does not depend on how the
	// nested reviews happen to be ordered.
	const review =
		program?.reviews?.find((candidate) => candidate.id === reviewId) ?? null;

	const { data: issueData, isLoading: isLoadingIssues } =
		useGetProgramIssuesQuery(
			{ program: programId, caught_phase: phase ?? 'review' },
			{ skip: !programId || !phase }
		);

	const [createIssue, { isLoading: isCreating }] =
		useCreateProgramIssueMutation();
	const [startReview, { isLoading: isStarting }] =
		useStartProgramReviewMutation();
	const [completeReview, { isLoading: isEnding }] =
		useCompleteProgramReviewMutation();
	const images = useProgramImages(programId);

	const issues = issueData?.results ?? [];
	const canLog = hasIssueDescription(description);

	const handleStartReview = async () => {
		try {
			await startReview({ program: programId }).unwrap();
		} catch {
			message.error('Could not start the review');
		}
	};

	const handleEndReview = async () => {
		if (!review) return;

		try {
			await completeReview({ id: review.id, programId }).unwrap();
			message.success('Review ended');
		} catch {
			message.error('Could not end the review');
		}
	};

	const handleLogIssue = async () => {
		if (!canLog || !phase || !review) {
			return;
		}

		try {
			await createIssue({
				program: programId,
				review: review.id,
				severity: DEFAULT_SEVERITY,
				caught_phase: phase,
				fault: DEFAULT_FAULT,
				insufficient_training: false,
				description,
			}).unwrap();
			setDescription('');
			setEditorKey((key) => key + 1);
			message.success('Issue logged');
		} catch {
			message.error('Failed to log issue');
		}
	};

	const lineItem = program?.order_line_item ?? null;
	const title = lineItem?.name || program?.filename || 'Program';
	const subtitle =
		[lineItem?.order_name, program?.filename]
			.filter((part): part is string => !!part && part !== title)
			.join(' \u00b7 ') || null;

	const body = () => {
		if (!programId || isProgramError) {
			return (
				<div className="embed-program-review__error">
					Unable to load this program
				</div>
			);
		}

		if (isLoadingProgram || !program || !phase) {
			return (
				<div className="embed-program-review__loading">Loading review…</div>
			);
		}

		if (!review) {
			return (
				<div className="embed-review-start">
					<p className="embed-review-start__hint">
						{REVIEW_PHASE_LABEL[phase]} has not been started for this program.
					</p>
					<Button
						type="primary"
						block
						loading={isStarting}
						onClick={handleStartReview}
					>
						Start Review
					</Button>
				</div>
			);
		}

		return (
			<>
				<div className="embed-program-review__editor">
					<RichTextEditor
						key={editorKey}
						content={description}
						onChange={setDescription}
						placeholder="Describe the issue… Paste or drop images directly."
						minHeight={104}
						{...images}
					/>
				</div>

				<Button
					type="primary"
					className="embed-program-review__log"
					disabled={!canLog || isCreating}
					loading={isCreating}
					onClick={handleLogIssue}
				>
					Log Issue
				</Button>

				<section className="embed-program-review__issues">
					<EmbedIssueList issues={issues} loading={isLoadingIssues} />
				</section>
			</>
		);
	};

	return (
		<div className="embed-program-review">
			<EmbedHeader
				title={title}
				subtitle={subtitle}
				phase={phase ?? undefined}
			>
				{review ? (
					<ReviewClock
						review={review}
						onEnd={handleEndReview}
						isEnding={isEnding}
					/>
				) : null}
			</EmbedHeader>
			{body()}
			{/* Last child on purpose: it pins itself to the foot of the panel,
			    which only reads as the bottom if nothing follows it. */}
			{review ? (
				<EmbedReviewFeedback
					review={review}
					programId={programId}
					images={images}
				/>
			) : null}
		</div>
	);
};

export default EmbedProgramIssueLogPage;
